(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.DominatorDealerMap = api;
}(typeof globalThis === 'object' ? globalThis : this, function (root) {
  'use strict';

  const DARK_MAP_STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#14213a' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#0d1424' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#526078' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#263650' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#aab6c8' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#071426' }] },
  ];

  function dealerMapPoints(matches) {
    if (!Array.isArray(matches)) return [];
    return matches.flatMap(function (match, index) {
      const latitude = match && match.latitude;
      const longitude = match && match.longitude;
      if (
        typeof latitude !== 'number' || !Number.isFinite(latitude) || latitude < -90 || latitude > 90 ||
        typeof longitude !== 'number' || !Number.isFinite(longitude) || longitude < -180 || longitude > 180
      ) return [];
      return [{
        index: index,
        id: String(match.location_id || match.id || ''),
        title: String(match.name || 'Approved partner'),
        position: { lat: latitude, lng: longitude },
      }];
    });
  }

  function loadGoogleMaps(apiKey, documentObject) {
    const key = typeof apiKey === 'string' ? apiKey.trim() : '';
    const document = documentObject || root.document;
    if (!key || !document || !document.head || typeof document.createElement !== 'function') return false;
    const selector = 'script[data-dominator-google-maps="true"]';
    if (typeof document.querySelector === 'function' && document.querySelector(selector)) return true;
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key);
    script.async = true;
    script.defer = true;
    script.dataset.dominatorGoogleMaps = 'true';
    document.head.appendChild(script);
    return true;
  }

  function createController(options) {
    const canvas = options && options.canvas;
    const fallback = options && options.fallback;
    const status = options && options.status;
    const apiKey = options && options.apiKey;
    const documentObject = options && options.document;
    const initialCenter = options && options.initialCenter
      ? options.initialCenter
      : { lat: 39.8283, lng: -98.5795 };
    const initialZoom = options && Number.isFinite(options.initialZoom)
      ? options.initialZoom
      : 4;
    const googleProvider = options && options.googleProvider
      ? options.googleProvider
      : function () { return root.google && root.google.maps; };
    const delay = options && options.delay
      ? options.delay
      : function () { return new Promise(function (resolve) { root.setTimeout(resolve, 200); }); };
    let map = null;
    let markers = [];
    let loaderRequested = false;

    async function mapsApi() {
      if (!loaderRequested && !googleProvider()) {
        loaderRequested = true;
        loadGoogleMaps(apiKey, documentObject);
      }
      for (let attempt = 0; attempt < 40; attempt += 1) {
        const value = googleProvider();
        if (value && value.Map && value.Marker && value.LatLngBounds) return value;
        await delay();
      }
      return null;
    }

    function clearMarkers() {
      markers.forEach(function (marker) { marker.setMap(null); });
      markers = [];
    }

    function setFallback(hidden) {
      if (!fallback) return;
      fallback.hidden = hidden;
      if (typeof fallback.setAttribute === 'function') {
        fallback.setAttribute('aria-hidden', String(hidden));
      }
    }

    function ensureMap(maps) {
      if (!map && canvas) {
        map = new maps.Map(canvas, {
          center: initialCenter,
          zoom: initialZoom,
          styles: DARK_MAP_STYLES,
          clickableIcons: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
      }
      return map;
    }

    async function initialize() {
      const maps = await mapsApi();
      if (!maps || !ensureMap(maps)) {
        setFallback(false);
        if (status) status.textContent = 'Map temporarily unavailable.';
        return { ready: false };
      }
      setFallback(true);
      if (status) status.textContent = 'Map ready. Search to view dealers.';
      return { ready: true };
    }

    async function update(matches, onSelect) {
      const points = dealerMapPoints(matches);
      clearMarkers();
      if (!points.length) {
        if (status) status.textContent = 'No mapped locations in these results.';
        return { mapped: 0 };
      }
      const maps = await mapsApi();
      if (!maps || !canvas) {
        setFallback(false);
        if (status) status.textContent = 'Map temporarily unavailable.';
        return { mapped: 0 };
      }
      ensureMap(maps);
      const bounds = new maps.LatLngBounds();
      markers = points.map(function (point) {
        bounds.extend(point.position);
        const marker = new maps.Marker({
          map: map,
          position: point.position,
          title: point.title,
        });
        if (typeof onSelect === 'function') {
          marker.addListener('click', function () { onSelect(point.index); });
        }
        return marker;
      });
      if (points.length === 1) {
        map.setCenter(points[0].position);
        map.setZoom(9);
      } else {
        map.fitBounds(bounds, 56);
      }
      setFallback(true);
      if (status) {
        status.textContent = points.length === 1
          ? '1 dealer location shown on the map.'
          : points.length + ' dealer locations shown on the map.';
      }
      return { mapped: points.length };
    }

    return { initialize: initialize, update: update };
  }

  function createControllerFromRoot(finderRoot, options) {
    const settings = options || {};
    const mapElement = finderRoot && typeof finderRoot.querySelector === 'function'
      ? finderRoot.querySelector('[data-dealer-map]')
      : null;
    const apiKey = mapElement && mapElement.dataset ? mapElement.dataset.mapApiKey : '';
    const centerLat = Number.parseFloat(mapElement && mapElement.dataset ? mapElement.dataset.mapCenterLat : '');
    const centerLng = Number.parseFloat(mapElement && mapElement.dataset ? mapElement.dataset.mapCenterLng : '');
    const mapZoom = Number.parseInt(mapElement && mapElement.dataset ? mapElement.dataset.mapZoom : '', 10);
    const googleProvider = settings.googleProvider
      ? settings.googleProvider
      : function () { return root.google && root.google.maps; };
    if (!googleProvider()) loadGoogleMaps(apiKey, settings.document);
    return createController({
      canvas: finderRoot && finderRoot.querySelector('[data-dealer-map-canvas]'),
      fallback: finderRoot && finderRoot.querySelector('[data-dealer-map-fallback]'),
      status: finderRoot && finderRoot.querySelector('[data-dealer-map-status]'),
      apiKey: apiKey,
      initialCenter: {
        lat: Number.isFinite(centerLat) ? centerLat : 39.8283,
        lng: Number.isFinite(centerLng) ? centerLng : -98.5795,
      },
      initialZoom: Number.isFinite(mapZoom) ? mapZoom : 4,
      document: settings.document,
      googleProvider: googleProvider,
      delay: settings.delay,
    });
  }

  return { createController: createController, createControllerFromRoot: createControllerFromRoot, dealerMapPoints: dealerMapPoints, loadGoogleMaps: loadGoogleMaps };
}));

